<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>
    <v-card>
      <v-card-title>{{ website_name }} Upstreams</v-card-title>
      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="upstreams"
        :search="search"
        :footer-props="{
          itemsPerPageOptions: [5, 10, 50, 100, 250, -1], // -> Add this example
          showFirstLastPage: false,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        item-key="id"
        class="elevation-1"
      >
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="500px">
              <template v-slot:activator="{ on }">
                <v-btn color="primary" dark class="mb-2" v-on="on"
                  >New Item</v-btn
                >
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-form ref="newForm" lazy-validation>
                    <v-container>
                      <v-row>
                        <v-text-field
                          v-model="editedItem.addr"
                          label="Address*"
                          :rules="ipRules"
                        ></v-text-field>
                      </v-row>
                      <v-row>
                        <v-text-field
                          v-model="editedItem.port"
                          label="Port*"
                          type="number"
                          :min=1
                          :rules="validPort"
                        ></v-text-field>
                      </v-row>
                      <v-row>
                        <v-text-field
                          v-model="editedItem.weight"
                          label="Weight*"
                          type="number"
                          :min="1"
                          :rules="[v => !!v || 'Weight is required', v => v > 0 || 'Weight must be greater than 0']"
                          ></v-text-field>
                      </v-row>
                      <v-row>
                        <v-checkbox
                          v-model="editedItem.tls"
                          label="TLS"
                        ></v-checkbox>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="close"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click="deleteItem(item)" v-on="on">mdi-delete</v-icon>
            </template>
            <span>Delete</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";
import ipAddr from "ip-address";

export default {
  mounted() {
    let website_id = this.$route.params.website_id;

    api
      .query(`beta/waf/websites/${website_id}/upstreams`)
      .then((response) => (this.upstreams = response.data.upstreams))
      .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
      .finally(() => (this.loading = false));

    this.website_name = this.$store.state.web.website_name;
  },

  data: () => ({
    selected: [],
    headers: [
      {
        text: "ID",
        align: "left",
        sortable: false,
        value: "id",
      },
      { text: "Address", value: "addr" },
      { text: "Port", value: "port" },
      { text: "Weight", value: "weight" },
      { text: "TLS", value: "tls" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    upstreams: [],
    errors: [],
    loading: true,
    dialog: false,
    editedItem: {
      id: "",
      addr: "",
      port: null,
      weight: null,
      tls: false,
    },
    defaultItem: {
      id: "",
      addr: "",
      port: null,
      weight: null,
      tls: false,
    },
    search: "",
    ipRules: [
      (v) => !!v || "IP address is required",
      (v) =>
        (!!v &&
          (new ipAddr.Address4(v).isValid() ||
            new ipAddr.Address6(v).isValid())) ||
        "IP address is invalid",
    ],
    validPort: [
      (v) => !!v || "Port is required",
      (v) =>
        !v ||
        (parseInt(v) !== NaN && parseInt(v) > 0 && parseInt(v) < 65536) ||
        "Must be between 1 and 65535",
    ],
    website_name: "",
  }),

  watch: {
    dialog(val) {
      val || this.close();
    },
  },

  methods: {
    deleteItem(item) {
      if (confirm("Are you sure you want to delete this item?")) {
        let website_id = this.$route.params.website_id;
        api
          .delete(`beta/waf/websites/${website_id}/upstreams/${item.id}`)
          .then((response) => {
            const index = this.upstreams.indexOf(item);
            this.upstreams.splice(index, 1);
          });
      }
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
      }, 300);
    },

    save() {
      if (!this.$refs.newForm.validate()) return;

      let website_id = this.$route.params.website_id;

      let newUpstream = {
        addr: this.editedItem.addr,
        port: this.editedItem.port,
        tls: this.editedItem.tls,    
        weight: this.editedItem.weight 
      }

      api
        .post(`beta/waf/websites/${website_id}/upstreams`, newUpstream)
        .then((response) => {
          this.upstreams = response.data.upstreams;
        })
        .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
              
      this.close();
    },
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Item" : "Edit Item";
    },
  },
};
</script>
